import React, { useEffect, useRef, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import PrivateImageCard from './PrivateImageCard';
import type { PrivateImageGridProps } from '@/hooks/content-generator/private-image-library/types';

interface ExtendedPrivateImageGridProps extends PrivateImageGridProps {
  workbookId?: string; // NEW: Pass workbook context for project assignment state
  hideStorageButton?: boolean; // NEW: Optional prop to hide storage buttons
}

const PrivateImageGrid: React.FC<ExtendedPrivateImageGridProps> = ({
  images,
  onImageClick,
  onLoadMore,
  hasMore,
  isLoading,
  workbookId, // NEW: Destructure workbookId
  hideStorageButton = false // NEW: Default to false for backward compatibility
}) => {
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  // Intersection Observer for infinite scroll
  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasMore && !isLoading) {
      onLoadMore();
    }
  }, [hasMore, isLoading, onLoadMore]);
  
  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0,
      rootMargin: '100px',
    });
    
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [handleIntersection]);
  
  return (
    <>
      {/* Image Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((image, index) => (
          <PrivateImageCard
            key={image.image_id}
            image={image}
            index={index}
            onClick={onImageClick}
            workbookId={workbookId} // NEW: Pass workbookId to PrivateImageCard
            hideStorageButton={hideStorageButton} // NEW: Pass hideStorageButton to PrivateImageCard
          />
        ))}
      </div>
      
      {/* Loading More Indicator */}
      {hasMore && (
        <div ref={loadMoreRef} className="flex justify-center py-8">
          {isLoading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading more images...</span>
            </div>
          ) : (
            <div className="text-gray-400 text-sm">Scroll to load more</div>
          )}
        </div>
      )}
    </>
  );
};

export default PrivateImageGrid; 